import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  FormField,
  Heading,
  Input,
  Modal,
  Select,
  Spinner,
  Table,
} from '@vwfs-bronson/bronson-react';
import { AuthTokens } from '../../auth';
import {
  Agent,
  FesConfig,
  GitHubPrTrigger,
  createGitHubPrTrigger,
  deleteGitHubPrTrigger,
  getAgents,
  getGitHubPrTriggers,
  updateGitHubPrTrigger,
} from '../../fes';
import { RowActionsMenu } from '../RowActionsMenu';

type TriggerForm = {
  triggerWord: string;
  agentId: string;
  enabled: boolean;
};

const emptyForm: TriggerForm = {
  triggerWord: '',
  agentId: '',
  enabled: true,
};

const triggerWordPattern = /^@[A-Za-z0-9][A-Za-z0-9_-]{0,62}$/;

export function GitHubPrTriggersPanel({
  fesConfig,
  teamId,
  tokens,
}: {
  fesConfig: FesConfig;
  teamId: string;
  tokens: AuthTokens;
}) {
  const [triggers, setTriggers] = useState<GitHubPrTrigger[]>([]);
  const [teamAgents, setTeamAgents] = useState<Agent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>();
  const [editingTrigger, setEditingTrigger] = useState<GitHubPrTrigger | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form, setForm] = useState<TriggerForm>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [triggerToDelete, setTriggerToDelete] = useState<GitHubPrTrigger | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>();

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(undefined);
    try {
      const [triggerResponse, agentResponse] = await Promise.all([
        getGitHubPrTriggers(fesConfig, tokens, teamId),
        getAgents(fesConfig, tokens),
      ]);
      setTriggers(triggerResponse.data || []);
      setTeamAgents((agentResponse.data || []).filter(agent => agent.teamId === teamId));
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Unable to load GitHub triggers.');
    } finally {
      setIsLoading(false);
    }
  }, [fesConfig, teamId, tokens]);

  useEffect(() => {
    void load();
  }, [load]);

  const agentsById = useMemo(
    () => new Map(teamAgents.map(agent => [agent.agentId, agent])),
    [teamAgents]
  );

  const openCreate = () => {
    setEditingTrigger(null);
    setForm({ ...emptyForm, agentId: teamAgents[0]?.agentId || '' });
    setSaveError(undefined);
    setIsFormOpen(true);
  };

  const openEdit = (trigger: GitHubPrTrigger) => {
    setEditingTrigger(trigger);
    setForm({
      triggerWord: trigger.triggerWord,
      agentId: trigger.agentId,
      enabled: trigger.enabled,
    });
    setSaveError(undefined);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    if (isSaving) return;
    setIsFormOpen(false);
    setEditingTrigger(null);
    setForm(emptyForm);
    setSaveError(undefined);
  };

  const save = async () => {
    if (!triggerWordPattern.test(form.triggerWord) || !form.agentId) return;
    setIsSaving(true);
    setSaveError(undefined);
    try {
      if (editingTrigger) {
        await updateGitHubPrTrigger(fesConfig, tokens, teamId, editingTrigger.triggerId, form);
      } else {
        await createGitHubPrTrigger(fesConfig, tokens, teamId, form);
      }
      setIsFormOpen(false);
      setEditingTrigger(null);
      setForm(emptyForm);
      await load();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Unable to save GitHub trigger.');
    } finally {
      setIsSaving(false);
    }
  };

  const remove = async () => {
    if (!triggerToDelete) return;
    setIsDeleting(true);
    setDeleteError(undefined);
    try {
      await deleteGitHubPrTrigger(fesConfig, tokens, teamId, triggerToDelete.triggerId);
      setTriggerToDelete(null);
      await load();
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : 'Unable to delete GitHub trigger.');
    } finally {
      setIsDeleting(false);
    }
  };

  const canSave =
    !isSaving &&
    triggerWordPattern.test(form.triggerWord) &&
    Boolean(form.agentId) &&
    teamAgents.some(agent => agent.agentId === form.agentId);

  return (
    <>
      <section className="app-team-panel" aria-labelledby="github-pr-triggers-heading">
        <div className="app-team-panel-header">
          <div>
            <div className="app-kicker">Team Detail</div>
            <Heading level={2} id="github-pr-triggers-heading" className="app-panel-title">
              GitHub triggers
            </Heading>
            <p className="app-panel-subtitle">
              Run a team agent when a new pull-request comment contains its trigger word. Comments
              matching more than one trigger are rejected.
            </p>
          </div>
          <div className="app-actions">
            <Button onClick={openCreate} disabled={isLoading || teamAgents.length === 0}>
              Create trigger
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="app-detail-loading" role="status">
            <Spinner center>Loading GitHub triggers...</Spinner>
          </div>
        ) : loadError ? (
          <div className="app-result app-team-error" role="alert">{loadError}</div>
        ) : teamAgents.length === 0 && triggers.length === 0 ? (
          <div className="app-empty-state">
            <strong>No team agents available</strong>
            <p>Create an agent assigned to this team before configuring a GitHub trigger.</p>
          </div>
        ) : triggers.length === 0 ? (
          <p className="app-empty">No GitHub triggers configured.</p>
        ) : (
          <div className="app-table-wrap">
            <Table className="app-table" colored>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Trigger word</Table.Th>
                  <Table.Th>Agent</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Created at</Table.Th>
                  <Table.Th className="app-table-actions-column">Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {triggers.map(trigger => {
                  const assignedAgent = agentsById.get(trigger.agentId);
                  return (
                    <Table.Tr key={trigger.triggerId}>
                      <Table.Td>{trigger.triggerWord}</Table.Td>
                      <Table.Td>
                        {assignedAgent ? `${assignedAgent.name} (${assignedAgent.agentId})` : trigger.agentId}
                      </Table.Td>
                      <Table.Td>
                        <Badge label={trigger.enabled ? 'Enabled' : 'Disabled'} modifier={trigger.enabled ? 'success' : undefined} />
                      </Table.Td>
                      <Table.Td>{trigger.createdAt}</Table.Td>
                      <Table.Td className="app-table-actions-column">
                        <RowActionsMenu
                          label={`GitHub trigger ${trigger.triggerWord}`}
                          onDelete={() => {
                            setTriggerToDelete(trigger);
                            setDeleteError(undefined);
                          }}
                          onEdit={() => openEdit(trigger)}
                        />
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </div>
        )}
      </section>

      {isFormOpen ? (
        <Modal
          buttonCancelLabel="Cancel GitHub trigger"
          buttonCancelText="Cancel"
          buttonCloseLabel="Close"
          buttonConfirmLabel={editingTrigger ? 'Save GitHub trigger' : 'Create GitHub trigger'}
          buttonConfirmText={isSaving ? 'Saving...' : editingTrigger ? 'Save' : 'Create'}
          confirmationDisabled={!canSave}
          onCancel={closeForm}
          onClose={closeForm}
          onConfirm={() => void save()}
          shown
          title={editingTrigger ? 'Edit GitHub trigger' : 'Create GitHub trigger'}
        >
          {saveError ? <div className="app-result app-team-error" role="alert">{saveError}</div> : null}
          <FormField labelForId="github-trigger-word" labelText="Trigger word" notion type="input">
            <Input
              id="github-trigger-word"
              value={form.triggerWord}
              onChange={event => setForm(current => ({ ...current, triggerWord: event.target.value }))}
              placeholder="@ACA-SECURITY"
              readOnly={isSaving}
            />
          </FormField>
          <p className="app-field-hint">Start with @ and use letters, numbers, hyphens, or underscores.</p>
          <FormField labelForId="github-trigger-agent" labelText="Team agent" notion type="select">
            <Select
              id="github-trigger-agent"
              value={form.agentId}
              onChange={event => setForm(current => ({ ...current, agentId: event.target.value }))}
              disabled={isSaving}
            >
              <Select.Item value="" disabled>Select a team agent</Select.Item>
              {teamAgents.map(agent => (
                <Select.Item key={agent.agentId} value={agent.agentId}>
                  {`${agent.name} (${agent.agentId})`}
                </Select.Item>
              ))}
            </Select>
          </FormField>
          <Checkbox
            id="github-trigger-enabled"
            checked={form.enabled}
            disabled={isSaving}
            onChange={event => setForm(current => ({ ...current, enabled: event.target.checked }))}
          >
            Enabled
          </Checkbox>
        </Modal>
      ) : null}

      {triggerToDelete ? (
        <Modal
          buttonCancelLabel="Cancel delete GitHub trigger"
          buttonCancelText="Cancel"
          buttonCloseLabel="Close"
          buttonConfirmLabel={`Delete GitHub trigger ${triggerToDelete.triggerWord}`}
          buttonConfirmText={isDeleting ? 'Deleting...' : 'Delete'}
          confirmationDisabled={isDeleting}
          onCancel={() => !isDeleting && setTriggerToDelete(null)}
          onClose={() => !isDeleting && setTriggerToDelete(null)}
          onConfirm={() => void remove()}
          shown
          status="warning"
          title="Delete GitHub trigger"
        >
          {deleteError ? <div className="app-result app-team-error" role="alert">{deleteError}</div> : null}
          <p>Delete <strong>{triggerToDelete.triggerWord}</strong>? New comments will no longer start its agent.</p>
        </Modal>
      ) : null}
    </>
  );
}
